/**
 * Collection / search pages: compact-on-scroll state for the sticky header.
 *
 * The header (.section-header) is already position:sticky in the theme. On
 * collection and search pages that only works now that the page-level
 * `overflow-x: hidden` guard lives on <html> alone (see the <style> blocks in
 * main-collection-product-grid.liquid and main-search.liquid) — with it on
 * <body>, body became the sticky containing block and the header scrolled
 * away with the page.
 *
 * This script only manages the compact state, styled by
 * component-plp-sticky-header.css:
 *
 *   - adds `.is-compact` once the shopper has scrolled past COMPACT_AT
 *   - removes it only when they return above EXPAND_AT
 *
 * The gap between the two thresholds (hysteresis) is deliberate: shrinking a
 * sticky header changes its height, which shifts the content beneath it. A
 * single toggle point would flicker right at the boundary.
 */
(function () {
  var COMPACT_AT = 80;
  var EXPAND_AT = 10;
  var COMPACT_CLASS = "is-compact";

  var header = document.querySelector(".section-header");
  if (!header) return;

  var compact = false;
  var ticking = false;

  /* ------------------------------------------------------------------
     Mobile compact state: the search bar under the header is hidden and a
     search icon (data-plp-search-toggle, rendered in header.liquid) stands
     in for it. Tapping the icon reveals the bar again and focuses its field.
     search-modal.js doesn't initialise on mobile, so this is self-contained.
     ------------------------------------------------------------------ */
  var SEARCH_OPEN_CLASS = "is-search-open";
  var searchToggle = document.querySelector("[data-plp-search-toggle]");
  var searchModal = header.querySelector("search-modal");

  function onDocumentClick(event) {
    if (searchModal && searchModal.contains(event.target)) return;
    if (searchToggle && searchToggle.contains(event.target)) return;
    closeSearch();
  }

  function onDocumentKeydown(event) {
    if (event.key === "Escape") closeSearch();
  }

  function openSearch() {
    header.classList.add(SEARCH_OPEN_CLASS);
    if (searchToggle) searchToggle.setAttribute("aria-expanded", "true");
    document.addEventListener("click", onDocumentClick, true);
    document.addEventListener("keydown", onDocumentKeydown);

    var input =
      searchModal &&
      searchModal.querySelector('.search-modal input[type="search"]');
    if (input) {
      // preventScroll: the theme's global smooth scrolling would otherwise
      // animate the page when the field takes focus.
      try {
        input.focus({ preventScroll: true });
      } catch (e) {
        input.focus();
      }
    }
  }

  function closeSearch() {
    if (!header.classList.contains(SEARCH_OPEN_CLASS)) return;
    header.classList.remove(SEARCH_OPEN_CLASS);
    if (searchToggle) searchToggle.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", onDocumentClick, true);
    document.removeEventListener("keydown", onDocumentKeydown);
  }

  if (searchToggle) {
    searchToggle.addEventListener("click", function (event) {
      event.preventDefault();
      if (header.classList.contains(SEARCH_OPEN_CLASS)) {
        closeSearch();
      } else {
        openSearch();
      }
    });
  }

  // While a drawer is open, the collection scroll-lock (and the size picker)
  // pin <body> with position:fixed; top:-N. Returns N — the scroll position
  // the page was at when it was pinned — or 0 when the page isn't pinned.
  function getBodyLockOffset() {
    var bodyStyle = document.body.style;
    if (bodyStyle.position !== "fixed") return 0;
    return -parseFloat(bodyStyle.top || "0") || 0;
  }

  function update() {
    ticking = false;

    // Leave the header alone while the theme's own search UI is open.
    if (header.classList.contains("search_active")) return;

    // Pinning the body drops the document's scroll position to 0 and fires
    // a scroll event. Reading window.scrollY then would un-compact the header
    // for as long as the drawer is open (and re-compact it on close). Use the
    // pre-lock position instead so the state carries through the drawer.
    var lockOffset = getBodyLockOffset();
    var y;
    if (lockOffset > 0) {
      y = lockOffset;
    } else if (getComputedStyle(document.body).position === "fixed") {
      return; // pinned with no known offset — hold the current state
    } else {
      y = window.scrollY || document.documentElement.scrollTop || 0;
    }

    if (!compact && y > COMPACT_AT) {
      compact = true;
      header.classList.add(COMPACT_CLASS);
    } else if (compact && y < EXPAND_AT) {
      compact = false;
      header.classList.remove(COMPACT_CLASS);
      // Back at the top the full bar is visible anyway; drop the toggle state.
      closeSearch();
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  window.addEventListener("scroll", onScroll, { passive: true });

  /* ------------------------------------------------------------------
     Keep the header on screen while a drawer pins the page.
     The collection/search scroll-lock (main-collection-product-grid /
     main-search) pins <body> with position:fixed; top:-N whenever the side
     menu, the filters or a cart drawer opens. That shifts everything — the
     sticky header included — N px up and off screen. For the side menu that
     traps the shopper: its "Close menu" control lives in the header.
     Mirror the lock offset onto the header. relative + top:N keeps it in
     normal flow (no layout jump) and, unlike transform, doesn't make the
     header the containing block for the menu's position:fixed panel.
     Cleared automatically when the lock releases.
     ------------------------------------------------------------------ */
  function syncHeaderToBodyLock() {
    var offset = getBodyLockOffset();

    if (offset > 0) {
      header.style.position = "relative";
      header.style.top = offset + "px";
    } else if (header.style.top) {
      header.style.position = "";
      header.style.top = "";
    }
  }

  if (window.MutationObserver) {
    new MutationObserver(syncHeaderToBodyLock).observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  // Pages can load already scrolled (back navigation, infinite-scroll
  // position restore), so evaluate once immediately.
  update();
  syncHeaderToBodyLock();
})();
